import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield, MessageSquare, Lock, Mail, FileText } from 'lucide-react';

const PrivacyPolicy = () => {
  const lastUpdated = "May 21, 2026";

  return (
    <div className="min-h-screen bg-black text-white pt-24 pb-16">
      <div className="container mx-auto px-4 max-w-4xl">
        <div className="mb-8">
          <Link
            to="/"
            data-testid="privacy-back-link"
            className="inline-flex items-center text-purple-400 hover:text-purple-300 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Link>
        </div>

        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-purple-900/40 border border-purple-500/40 mb-4">
            <Shield className="w-8 h-8 text-purple-400" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">Privacy Policy</h1>
          <p className="text-gray-400">Last Updated: {lastUpdated}</p>
        </div>

        <div className="space-y-8 text-gray-300 leading-relaxed">
          <section className="bg-purple-900/10 border border-purple-500/20 rounded-xl p-6">
            <h2 className="text-2xl font-bold text-white mb-3 flex items-center gap-2">
              <FileText className="w-6 h-6 text-purple-400" />
              Overview
            </h2>
            <p>
              High Quality Studios LLC ("HQ Studios," "we," "our," "us") operates the website{' '}
              <a href="https://hqstu.com" className="text-purple-400 hover:underline">hqstu.com</a>{' '}
              and provides music recording, mixing, mastering, and engineering services from our
              studio at 270 Meserole Street, Brooklyn NY 11206. This Privacy Policy explains how we
              collect, use, share, and protect your information.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-3">1. Information We Collect</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong className="text-white">Contact info:</strong> name, email, phone number, when you create an account or book a session.</li>
              <li><strong className="text-white">Booking info:</strong> session date, time, engineer, services purchased, duration, notes.</li>
              <li><strong className="text-white">Payment info:</strong> processed securely by Stripe. We never see or store your full card number.</li>
              <li><strong className="text-white">Files you upload:</strong> beats, stems, references for mix/master, stored on secure servers.</li>
              <li><strong className="text-white">Usage data:</strong> pages visited, device type, IP address, for analytics and security.</li>
            </ul>
          </section>

          <section className="bg-emerald-900/10 border border-emerald-500/30 rounded-xl p-6">
            <h2 className="text-2xl font-bold text-white mb-3 flex items-center gap-2">
              <MessageSquare className="w-6 h-6 text-emerald-400" />
              2. SMS / Text Messaging Consent
            </h2>
            <p className="mb-3">
              By providing your phone number on our booking form and checking the SMS-consent
              checkbox, you expressly consent to receive recurring transactional and promotional
              text messages from High Quality Studios LLC, including:
            </p>
            <ul className="list-disc pl-6 space-y-1 mb-3">
              <li>Booking confirmation after payment</li>
              <li>Booking approval / decline notices from your assigned engineer</li>
              <li>Appointment reminders 24 hours and 3 hours before each session</li>
              <li>Occasional promotional offers (deals, specials) to past customers</li>
            </ul>
            <p className="mb-3">
              <strong className="text-white">Message frequency varies</strong> based on your booking
              activity, typically 2–6 messages per booking. <strong className="text-white">
              Message and data rates may apply</strong> based on your mobile carrier.
            </p>
            <p className="mb-3">
              <strong className="text-white">To opt out at any time,</strong> reply <span className="bg-emerald-700/40 px-1.5 py-0.5 rounded font-mono text-emerald-200">STOP</span> to
              any text from us. To resume messages, reply{' '}
              <span className="bg-emerald-700/40 px-1.5 py-0.5 rounded font-mono text-emerald-200">START</span>.
              For help, reply <span className="bg-emerald-700/40 px-1.5 py-0.5 rounded font-mono text-emerald-200">HELP</span>{' '}
              or email <a href="mailto:trapspade@hqstu.com" className="text-emerald-300 underline">trapspade@hqstu.com</a>.
            </p>
            <p className="mb-3">
              <strong className="text-white">No mobile information will be shared with third parties or affiliates for marketing or promotional purposes.</strong>{' '}
              All other categories exclude text messaging originator opt-in data and consent;
              this information will not be shared with any third parties.
            </p>
            <p>
              We do not share, sell, rent, or lease your phone number or SMS opt-in data to any
              third party for marketing purposes. Carriers (T-Mobile, Verizon, AT&amp;T, etc.) are not
              liable for delayed or undelivered messages.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-3">3. How We Use Your Information</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>To create and manage your account.</li>
              <li>To process bookings and payments.</li>
              <li>To send booking confirmations, reminders, and service updates via email and SMS.</li>
              <li>To improve our services and site.</li>
              <li>To respond to your inquiries.</li>
              <li>To comply with legal obligations.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-3">4. How We Share Your Information</h2>
            <p className="mb-3">We share only with these service providers, strictly to operate our service:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong className="text-white">Stripe:</strong> payment processing</li>
              <li><strong className="text-white">SendGrid:</strong> transactional email delivery</li>
              <li><strong className="text-white">Twilio:</strong> SMS delivery (with your consent)</li>
              <li><strong className="text-white">Wave:</strong> invoicing and accounting</li>
              <li><strong className="text-white">MongoDB Atlas:</strong> encrypted database hosting</li>
            </ul>
            <p className="mt-3">
              We <strong className="text-white">never</strong> sell your data. We disclose information
              only if legally required (subpoena, court order) or to prevent fraud.
            </p>
          </section>

          <section className="bg-purple-900/10 border border-purple-500/20 rounded-xl p-6">
            <h2 className="text-2xl font-bold text-white mb-3 flex items-center gap-2">
              <Lock className="w-6 h-6 text-purple-400" />
              5. Data Security
            </h2>
            <p>
              All data is transmitted over HTTPS (TLS encryption). Passwords are hashed with bcrypt.
              Payment data is processed by Stripe (PCI-DSS Level 1 certified). Database access is
              restricted to authorized personnel only.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-3">6. Your Rights</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong className="text-white">Access:</strong> request a copy of the data we hold about you.</li>
              <li><strong className="text-white">Correction:</strong> ask us to fix anything inaccurate.</li>
              <li><strong className="text-white">Deletion:</strong> ask us to delete your account and all associated data.</li>
              <li><strong className="text-white">Opt-out of SMS:</strong> reply STOP to any text.</li>
              <li><strong className="text-white">Opt-out of email:</strong> click "unsubscribe" in any marketing email.</li>
            </ul>
            <p className="mt-3">
              Contact <a href="mailto:trapspade@hqstu.com" className="text-purple-400 underline">
              trapspade@hqstu.com</a> to exercise any of these rights.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-3">7. Cookies</h2>
            <p>
              We use minimal cookies for authentication (keeping you logged in) and basic analytics.
              We do not use third-party advertising cookies.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-3">8. Children's Privacy</h2>
            <p>
              Our service is not directed to children under 13. We do not knowingly collect data
              from anyone under 13. If we learn that we have collected such data, we will delete it.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-3">9. Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. The "Last Updated" date at the
              top of this page reflects the most recent revision. For material changes, we will
              notify you by email or on this page.
            </p>
          </section>

          <section className="bg-purple-900/10 border border-purple-500/20 rounded-xl p-6">
            <h2 className="text-2xl font-bold text-white mb-3 flex items-center gap-2">
              <Mail className="w-6 h-6 text-purple-400" />
              10. Contact Us
            </h2>
            <p className="mb-2"><strong className="text-white">High Quality Studios LLC</strong></p>
            <p>270 Meserole Street, Brooklyn NY 11206</p>
            <p>Email: <a href="mailto:trapspade@hqstu.com" className="text-purple-400 underline">trapspade@hqstu.com</a></p>
            <p>Phone: <a href="tel:+19294131993" className="text-purple-400 underline">929-413-1993</a></p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
